import { ChevronLeft, ChevronRight, Stethoscope } from 'lucide-react';
import { useMedplum } from '@medplum/react-hooks';
import { NavLink } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import { isPracticeAdmin } from '../../features/admin/isPracticeAdmin';
import { cn } from '../../lib/utils';
import { navItems } from './nav-items';
import { VersionBadge } from './VersionBadge';

/**
 * ACTO chrome sidebar — indigo-accent active state, collapsible rail. Ported
 * from modern-accounting's Sidebar/NavItem conventions onto ACTO tokens.
 *
 * Responsive (#83): at `md` and up it is the in-flow rail (collapsible). Below `md`
 * it becomes an off-canvas drawer — hidden until the header's menu button opens
 * it, over a backdrop, and it closes on navigation — so a phone gets the full
 * width for the chart instead of a squeezed column.
 */
export function Sidebar() {
  const { isCollapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const medplum = useMedplum();
  // The drawer always shows labels; the desktop rail honors the collapsed preference.
  const collapsed = isCollapsed && !mobileOpen;
  // Hide admin-only items (Admin) from non-admins. This is UI convenience, not security — the server
  // AccessPolicy is what actually blocks a non-admin from changing practice settings (#142).
  const items = isPracticeAdmin(medplum) ? navItems : navItems.filter((item) => !item.adminOnly);

  return (
    <>
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
          data-testid="sidebar-backdrop"
        />
      ) : null}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-full w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-200',
          'md:static md:z-auto md:translate-x-0 md:transition-all',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'md:w-16' : 'md:w-60'
        )}
        data-testid="sidebar"
        data-mobile-open={mobileOpen ? 'true' : 'false'}
      >
        {/* Brand */}
        <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-4">
          <Stethoscope className="h-6 w-6 shrink-0 text-primary" aria-hidden />
          {!collapsed ? (
            <span className="text-base font-semibold text-foreground">
              Fluent<span className="font-normal"> MedOps</span>
            </span>
          ) : null}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.id}
                to={item.to}
                title={collapsed ? item.label : undefined}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground hover:bg-muted hover:text-foreground',
                    collapsed && 'justify-center px-0'
                  )
                }
              >
                <Icon className="h-5 w-5 shrink-0" aria-hidden />
                {!collapsed ? <span>{item.label}</span> : null}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer: build stamp + collapse toggle (toggle is desktop rail only) */}
        <div className="border-t border-sidebar-border p-2">
          <VersionBadge collapsed={collapsed} />
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground md:flex"
          >
            {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            {!isCollapsed ? <span>Collapse</span> : null}
          </button>
        </div>
      </aside>
    </>
  );
}
